import React from "react"
import Supercluster from "supercluster"
import { useMap } from "@vis.gl/react-google-maps"
import { BikesCluster, SUPERCLUSTER_OPTIONS } from "../lib/helpers/clusters"
import { MapBikeEntity } from "../lib/hooks/queries/useGetMapBikesQuery"
import { useGoogleMapsEffect } from "../lib/hooks/useGoogleMapsEffect"
import { createBBoxFromGoogleMapBounds } from "../lib/helpers/googleMap"
import BikesMapGoogleSuperclusterMarker from "./BikesMapGoogleSuperclusterMarker"
import { getBikeFeatures } from "../lib/helpers/map"

interface BikesMapGoogleSuperclusterProps {
  mapId: string
  bikes: MapBikeEntity[]
  activeBike: MapBikeEntity | null
}

const _BikesMapGoogleSupercluster: React.FC<BikesMapGoogleSuperclusterProps> = ({
  mapId,
  bikes,
  activeBike,
}) => {
  const [superCluster, setSuperCluster] = React.useState<Supercluster>()
  const [currentClusters, setCurrentClusters] = React.useState<BikesCluster[]>()
  const currentClustersLength = currentClusters?.length
  const map = useMap(mapId)

  const updateSupecluster = React.useCallback(
    ({
      superClusterInstance,
      forceSetState,
    }: {
      superClusterInstance: Supercluster | undefined
      forceSetState?: boolean
    }) => {
      if (!map || !superClusterInstance) return
      const bounds = map?.getBounds()
      const zoom = map?.getZoom()
      if (!bounds || !zoom) return
      const bbox = createBBoxFromGoogleMapBounds(bounds)
      const clusters = superClusterInstance.getClusters(
        [...bbox[0], ...bbox[1]],
        Math.round(zoom),
      ) as BikesCluster[]

      // check if changed length
      if (clusters.length !== currentClustersLength || forceSetState) {
        setCurrentClusters(clusters)
      }
    },
    [map, currentClustersLength],
  )

  // init supercluster
  useGoogleMapsEffect(
    mapId,
    () => {
      const points = bikes?.map(getBikeFeatures) ?? []
      const cluster = new Supercluster({
        ...SUPERCLUSTER_OPTIONS,
        extent: 256,
      })
      cluster.load(points)
      setSuperCluster(cluster)
      updateSupecluster({ superClusterInstance: cluster, forceSetState: true })
    },
    [bikes],
  )

  // update clusters when map is idle
  useGoogleMapsEffect(
    mapId,
    () => {
      if (!superCluster) return
      const listener = google.maps.event.addListener(map!, "idle", () =>
        updateSupecluster({ superClusterInstance: superCluster }),
      )
      return () => {
        listener.remove()
      }
    },
    [superCluster],
  )

  return (
    <>
      {currentClusters?.map((cluster) => {
        const key = cluster.properties.cluster
          ? `cluster_${cluster.id}`
          : `asset_${cluster.properties.bicycle?.uuid}`

        return (
          <BikesMapGoogleSuperclusterMarker
            key={key}
            mapId={mapId}
            currentCluster={cluster}
            superClusterInstance={superCluster}
            activeBike={activeBike}
          />
        )
      })}
    </>
  )
}

const BikesMapGoogleSupercluster = React.memo(_BikesMapGoogleSupercluster)

export default BikesMapGoogleSupercluster
